import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ChatPanel from '../components/ChatPanel';
import VersionHistoryPanel from '../components/VersionHistoryPanel';
import FeedbackPanel from '../components/FeedbackPanel';
import { FollowUpQuestionsPanel, AnalysisNotesPanel, normalizeQuestions } from '../components/FollowUpQuestionsPanel';
import { SLOW_AFTER_MIN } from '../components/upload/JobProgressTracker';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  getSowDraft, regenerateSow, getJobStatus, applyDocContent,
  exportSowWord, exportSowPdf, downloadExport, saveSowAnswers,
} from '../services/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, Loader2, FileText, Download, RefreshCw, FileDown,
  History, MessageSquarePlus, ChevronRight, Sparkles, Pencil,
  Save, X, Building2, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Why Regenerate does nothing on a document that came in through Import document.
const NO_REGENERATE_EXPLANATION = 'DocHub holds no source files for this document (no ticket, emails or brief were '
  + 'uploaded with it), so there is nothing to regenerate it from. Edit it on this page or through the chat instead; '
  + 'each change is saved as a new version.';

// ---------------------------------------------------------------------------
// Table of Contents — extracts headings from markdown (mirrors ProjectDetailPage)
// ---------------------------------------------------------------------------
const extractHeadings = (content) => {
  if (!content) return [];
  const headings = [];
  const lines = content.split('\n');
  for (const line of lines) {
    const match = line.match(/^(#{1,4})\s+(.+)/);
    if (match) {
      const level = match[1].length;
      const text = match[2].replace(/\*\*/g, '').replace(/\*/g, '').trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      headings.push({ level, text, id });
    }
  }
  return headings;
};

// Recursively flatten ReactMarkdown children to plain text so a heading's
// element id matches extractHeadings' slug even when the heading contains
// inline formatting (bold/link/code) — children?.toString() yields
// '[object Object]' for element arrays and breaks ToC scroll/highlight.
const childrenToText = (children) => {
  if (children == null) return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(childrenToText).join('');
  if (children.props && children.props.children != null) return childrenToText(children.props.children);
  return '';
};

const TableOfContents = ({ content, activeId }) => {
  const headings = useMemo(() => extractHeadings(content), [content]);
  if (headings.length < 2) return null;

  const handleClick = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav className="space-y-0.5" data-testid="sow-table-of-contents">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3 px-2">
        On this page
      </p>
      {headings.map((h, i) => (
        <button
          key={i}
          onClick={() => handleClick(h.id)}
          className={`block w-full text-left text-[13px] leading-snug py-1 px-2 rounded transition-colors truncate ${
            activeId === h.id
              ? 'text-[#2B8FD8] font-medium bg-blue-50/60'
              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
          }`}
          style={{ paddingLeft: `${(h.level - 1) * 12 + 8}px` }}
          title={h.text}
        >
          {h.text}
        </button>
      ))}
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Markdown components — Notion-inspired typography (mirrors ProjectDetailPage)
// ---------------------------------------------------------------------------
const createMarkdownComponents = (onFeedbackClick) => ({
  h1: ({ children }) => {
    const text = childrenToText(children);
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (
      <h1 id={id} className="group text-[28px] font-bold text-slate-900 mt-12 mb-5 pb-3 border-b border-slate-200/80 font-['Plus_Jakarta_Sans'] first:mt-0 scroll-mt-36 relative">
        {children}
        {onFeedbackClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onFeedbackClick(text); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-[#2B8FD8] text-[10px] font-medium hover:bg-blue-100 align-middle"
            title="Leave feedback on this section"
          >
            <MessageSquarePlus className="h-3 w-3" />
          </button>
        )}
      </h1>
    );
  },
  h2: ({ children }) => {
    const text = childrenToText(children);
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (
      <h2 id={id} className="group text-[22px] font-semibold text-slate-800 mt-10 mb-4 pb-2 border-b border-slate-100 font-['Plus_Jakarta_Sans'] scroll-mt-36 relative">
        {children}
        {onFeedbackClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onFeedbackClick(text); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-[#2B8FD8] text-[10px] font-medium hover:bg-blue-100 align-middle"
            title="Leave feedback on this section"
          >
            <MessageSquarePlus className="h-3 w-3" />
          </button>
        )}
      </h2>
    );
  },
  h3: ({ children }) => {
    const text = childrenToText(children);
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (
      <h3 id={id} className="group text-lg font-semibold text-slate-800 mt-8 mb-3 font-['Plus_Jakarta_Sans'] scroll-mt-36 relative">
        {children}
        {onFeedbackClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onFeedbackClick(text); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-[#2B8FD8] text-[10px] font-medium hover:bg-blue-100 align-middle"
            title="Leave feedback on this section"
          >
            <MessageSquarePlus className="h-3 w-3" />
          </button>
        )}
      </h3>
    );
  },
  h4: ({ children }) => {
    const text = childrenToText(children);
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return (
      <h4 id={id} className="text-base font-semibold text-slate-700 mt-6 mb-2 font-['Plus_Jakarta_Sans'] scroll-mt-36">
        {children}
      </h4>
    );
  },
  p: ({ children }) => (
    <p className="text-[15px] leading-[1.8] text-slate-600 my-4">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-4 ml-1 space-y-2 list-none">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 ml-1 space-y-2 list-decimal list-inside marker:text-slate-400 marker:font-medium">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-[15px] leading-[1.8] text-slate-600 pl-1 relative before:content-[''] before:absolute before:left-[-12px] before:top-[11px] before:w-[5px] before:h-[5px] before:rounded-full before:bg-slate-300">{children}</li>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-[3px] border-[#2B8FD8]/40 bg-sky-50/40 pl-5 pr-4 py-3 my-6 rounded-r text-slate-600">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-8 rounded-lg border border-slate-200">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50/80 border-b border-slate-200">{children}</thead>,
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 text-sm text-slate-600 border-t border-slate-100">{children}</td>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-slate-50/50 transition-colors">{children}</tr>
  ),
  hr: () => <hr className="my-10 border-slate-200" />,
  a: ({ href, children }) => (
    <a href={href} className="text-[#2B8FD8] hover:text-[#2075b3] underline underline-offset-2 decoration-[#2B8FD8]/30 hover:decoration-[#2B8FD8] transition-colors" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-slate-800">{children}</strong>,
  em: ({ children }) => <em className="text-slate-600">{children}</em>,
  code: ({ inline, className, children }) => {
    if (inline) {
      return (
        <code className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[13px] font-mono border border-slate-200/60">
          {children}
        </code>
      );
    }
    const lang = className?.replace('language-', '') || '';
    return (
      <div className="my-6 rounded-lg overflow-hidden border border-slate-200/60 shadow-sm">
        {lang && (
          <div className="bg-slate-800 px-4 py-1.5 text-[10px] font-mono text-slate-400 uppercase tracking-wider border-b border-slate-700/30">
            {lang}
          </div>
        )}
        <pre className="bg-[#1e1e2e] text-slate-200 p-5 overflow-x-auto">
          <code className="text-[13px] leading-6 font-mono">{children}</code>
        </pre>
      </div>
    );
  },
});

// ---------------------------------------------------------------------------
// SOW Draft Page — review / edit / regenerate / download a single SOW document
// ---------------------------------------------------------------------------
const SowDraftPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, token } = useAuth();

  const [sow, setSow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [regenElapsedMin, setRegenElapsedMin] = useState(0);
  const regenTimerRef = useRef(null);
  const pollRef = useRef(null);

  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSection, setFeedbackSection] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [openFeedbackCount, setOpenFeedbackCount] = useState(0);

  // Inline markdown editing (409-safe apply)
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  const [activeTocId, setActiveTocId] = useState('');

  const isRightPanelOpen = chatOpen || versionHistoryOpen || feedbackOpen;

  // ---- Feedback handler (mirrors ProjectDetailPage) ----
  const handleSectionFeedback = useCallback((sectionText) => {
    setFeedbackSection(sectionText);
    setFeedbackOpen(true);
    setChatOpen(false);
    setVersionHistoryOpen(false);
  }, []);

  const markdownComponents = useMemo(
    () => createMarkdownComponents(canEdit && !editing ? handleSectionFeedback : null),
    [canEdit, editing, handleSectionFeedback]
  );

  // ---- Load the SOW ----
  const loadSow = useCallback(async () => {
    try {
      const res = await getSowDraft(id);
      setSow(res.data);
      if (res.data.feedback_counts) {
        setOpenFeedbackCount(res.data.feedback_counts.open || 0);
      }
    } catch (error) {
      toast.error('Failed to load SOW draft');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadSow(); }, [loadSow]);

  // Cleanup regen timer on unmount
  useEffect(() => () => {
    if (regenTimerRef.current) clearInterval(regenTimerRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  // ---- Track active heading for ToC highlight ----
  useEffect(() => {
    const content = sow?.content;
    if (!content || editing) return;
    const headings = extractHeadings(content);
    if (headings.length < 2) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveTocId(entry.target.id);
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );

    const timer = setTimeout(() => {
      headings.forEach(h => {
        const el = document.getElementById(h.id);
        if (el) observer.observe(el);
      });
    }, 200);

    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [sow?.content, editing]);

  const handleFeedbackCountChange = useCallback((openCount) => {
    setOpenFeedbackCount(openCount);
  }, []);

  // ---- Regenerate (reuses job-status polling pattern) ----
  const handleRegenerate = async (answers = null) => {
    if (!sow || regenerating) return;
    // An imported document has no stored source files (ticket, emails, brief),
    // so the server has nothing to regenerate it from — say so instead of failing.
    if (sow.origin === 'import') {
      toast.info('This SOW cannot be regenerated', {
        description: NO_REGENERATE_EXPLANATION,
        duration: 12000,
      });
      return;
    }
    if (!window.confirm(answers
      ? 'Regenerate with your answers? A new version will be created.'
      : 'Regenerate this SOW draft? A new version will be created.')) return;
    setRegenerating(true);
    setRegenElapsedMin(0);
    const startTime = Date.now();
    if (regenTimerRef.current) clearInterval(regenTimerRef.current);
    regenTimerRef.current = setInterval(() => {
      setRegenElapsedMin(Math.floor((Date.now() - startTime) / 60000));
    }, 30000);
    try {
      const res = await regenerateSow(sow.id, answers || undefined);
      const jobId = res.data.job_id;
      toast.info('SOW regeneration started...');
      const stopPolling = () => {
        clearInterval(pollRef.current); pollRef.current = null;
        clearInterval(regenTimerRef.current); regenTimerRef.current = null;
        setRegenerating(false); setRegenElapsedMin(0);
      };
      let pollFails = 0;
      pollRef.current = setInterval(async () => {
        try {
          const jobRes = await getJobStatus(jobId);
          const job = jobRes.data;
          pollFails = 0;
          if (job.status === 'complete') {
            stopPolling();
            toast.success('SOW regenerated!');
            // Regenerate creates a NEW document (fresh id); navigate to it so the
            // user sees the new draft rather than reloading the superseded one.
            const newId = job.documentation_id;
            if (newId && newId !== id) {
              navigate(`/sow-builder/${newId}`);
            } else {
              await loadSow();
            }
          } else if (job.status === 'failed') {
            stopPolling();
            if (job.error && (job.error.includes('interrupted') || job.error.includes('timeout'))) {
              toast.error('Regeneration was interrupted. Please try again.');
            } else {
              toast.error(job.error || 'Regeneration failed');
            }
          }
        } catch (err) {
          // Tolerate transient poll failures (network blip, 5xx, silent token
          // refresh) — the backend job keeps running. Only give up after several
          // consecutive failures so a single hiccup doesn't falsely abort.
          pollFails += 1;
          console.error('SOW regenerate poll error', err);
          if (pollFails >= 5) {
            stopPolling();
            toast.error('Lost connection to regeneration job');
          }
        }
      }, 3000);
    } catch (error) {
      clearInterval(regenTimerRef.current); regenTimerRef.current = null;
      setRegenerating(false); setRegenElapsedMin(0);
      toast.error(error.response?.data?.detail || 'Failed to start regeneration');
    }
  };

  // ---- Inline edit (409-safe applyDocContent) ----
  const startEditing = () => {
    if (!sow) return;
    setEditContent(sow.content || '');
    setEditing(true);
  };
  const cancelEditing = () => { setEditing(false); setEditContent(''); };

  const handleSaveEdit = async () => {
    if (!sow || saving) return;
    setSaving(true);
    try {
      await applyDocContent(sow.id, editContent, sow.version);
      toast.success('SOW updated!');
      setEditing(false);
      setEditContent('');
      await loadSow();
    } catch (error) {
      if (error.response?.status === 409) {
        // Concurrent edit — someone/something bumped the version. Reload and warn.
        toast.error('This SOW changed since you started editing. Reloading the latest version — please re-apply your edit.');
        await loadSow();
        setEditing(false);
        setEditContent('');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save changes');
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- Authenticated downloads (shared helper; a failure toasts the server's reason) ----
  const exportAs = (url, ext, label) => {
    toast.promise(downloadExport(url, token, `${sow.title}.${ext}`), {
      loading: `Preparing SOW ${label}…`,
      success: `SOW exported as ${label}!`,
      error: (err) => err.message,
    });
  };
  const handleExportWord = () => { if (sow && token) exportAs(exportSowWord(sow.id), 'docx', 'Word'); };
  const handleExportPdf = () => { if (sow && token) exportAs(exportSowPdf(sow.id), 'pdf', 'PDF'); };

  const handleDocUpdated = async () => { await loadSow(); };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  if (!sow) return null;

  const content = sow.content || '';
  const followUps = normalizeQuestions(sow.sow_draft?.follow_up_questions);

  const rightPanelPad = chatOpen
    ? 'lg:pr-[380px] xl:pr-[420px]'
    : versionHistoryOpen
      ? 'lg:pr-[480px] xl:pr-[560px]'
      : feedbackOpen
        ? 'lg:pr-[420px]'
        : '';

  return (
    <Layout>
      <div className={`flex flex-col min-h-screen transition-[padding-right] duration-300 ${rightPanelPad}`}>
        {/* Header — breadcrumb bar */}
        <div className="px-3 md:px-6 py-2.5 md:py-3 border-b border-slate-200 bg-white sticky top-0 z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 md:gap-2 text-sm min-w-0">
              <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-[#2B8FD8] transition-colors flex items-center gap-1 shrink-0">
                <ArrowLeft className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Back</span>
              </button>
              <ChevronRight className="h-3 w-3 text-slate-300 shrink-0" />
              <div className="flex items-center gap-1.5 min-w-0">
                <FileText className="h-4 w-4 text-[#2B8FD8] shrink-0" />
                <span className="font-semibold text-slate-900 truncate" data-testid="sow-title">{sow.title}</span>
              </div>
              <Badge variant="outline" className="text-[10px] ml-1 shrink-0">SOW</Badge>
              <Badge variant="outline" className="text-[10px] shrink-0">v{sow.version}</Badge>
              {sow.customer_name && (
                <span className="text-slate-400 items-center gap-1 hidden xl:flex ml-1">
                  <Building2 className="h-3 w-3" />{sow.customer_name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Doc toolbar — slim action bar */}
        <div className="px-3 md:px-6 py-2 md:py-2.5 bg-white border-b border-slate-100 flex items-center justify-end gap-1 md:gap-1.5 overflow-x-auto">
          {editing ? (
            <>
              <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving} data-testid="sow-cancel-edit-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3">
                <X className="h-3.5 w-3.5" /><span className="hidden md:inline ml-1">Cancel</span>
              </Button>
              <Button size="sm" onClick={handleSaveEdit} disabled={saving} data-testid="sow-save-edit-btn" className="h-7 text-xs bg-[#2B8FD8] hover:bg-[#2075b3] px-1.5 md:px-3">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                <span className="hidden md:inline ml-1">{saving ? 'Saving...' : 'Save'}</span>
              </Button>
            </>
          ) : (
            <>
              {canEdit && (
                <Button variant="ghost" size="sm" onClick={startEditing} data-testid="sow-edit-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3">
                  <Pencil className="h-3.5 w-3.5" /><span className="hidden xl:inline ml-1">Edit</span>
                </Button>
              )}
              {canEdit && (
                <Button variant="ghost" size="sm" onClick={() => handleRegenerate()} disabled={regenerating} data-testid="sow-regenerate-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3">
                  {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  <span className="hidden xl:inline ml-1">{regenerating ? 'Regenerating...' : 'Regenerate'}</span>
                </Button>
              )}
              {sow?.sow_draft_stale && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] text-amber-600 px-1.5"
                  title={sow.origin === 'import'
                    ? "Your manual edits aren't included in the branded Word/PDF — it rebuilds from the saved draft, and this SOW has no source files to regenerate from."
                    : "Your manual edits aren't included in the branded Word/PDF — it rebuilds from the last AI generation. Regenerate to fold your edits in."}
                  data-testid="sow-stale-export-warning"
                >
                  <AlertTriangle className="h-3.5 w-3.5" /><span className="hidden xl:inline">edits not in export</span>
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={handleExportWord} data-testid="sow-export-word-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3"
                title={sow?.sow_draft_stale ? 'Branded export reflects the last AI generation, not your manual edits' : 'Download branded Word'}>
                <Download className="h-3.5 w-3.5" /><span className="hidden xl:inline ml-1">Word</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={handleExportPdf} data-testid="sow-export-pdf-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3"
                title={sow?.sow_draft_stale ? 'Branded export reflects the last AI generation, not your manual edits' : 'Download branded PDF'}>
                <FileDown className="h-3.5 w-3.5" /><span className="hidden xl:inline ml-1">PDF</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setVersionHistoryOpen(true); setChatOpen(false); setFeedbackOpen(false); }} data-testid="sow-version-history-btn" className="h-7 text-xs text-slate-500 px-1.5 md:px-3">
                <History className="h-3.5 w-3.5" /><span className="hidden md:inline ml-1">History</span>
              </Button>
              <Button variant="ghost" size="sm"
                onClick={() => { setFeedbackOpen(true); setFeedbackSection(''); setChatOpen(false); setVersionHistoryOpen(false); }}
                data-testid="sow-feedback-btn"
                className="h-7 text-xs text-slate-500 px-1.5 md:px-3 relative"
              >
                <MessageSquarePlus className="h-3.5 w-3.5" /><span className="hidden xl:inline ml-1">Feedback</span>
                {openFeedbackCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-[#2B8FD8] text-white text-[9px] font-bold flex items-center justify-center px-1">
                    {openFeedbackCount}
                  </span>
                )}
              </Button>
            </>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 flex relative bg-slate-50/50">
          <div className="flex-1 min-w-0 flex">
            {/* Center content */}
            <div className="flex-1 min-w-0">
              {/* Regeneration in progress banner */}
              {regenerating && (
                <div className="max-w-3xl mx-auto px-4 md:px-10 pt-4">
                  <div className={`rounded-lg px-4 py-3 flex items-start gap-3 ${regenElapsedMin >= SLOW_AFTER_MIN ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'}`}>
                    <Loader2 className={`h-4 w-4 animate-spin mt-0.5 shrink-0 ${regenElapsedMin >= SLOW_AFTER_MIN ? 'text-amber-600' : 'text-blue-600'}`} />
                    <div>
                      <p className={`text-sm font-medium ${regenElapsedMin >= SLOW_AFTER_MIN ? 'text-amber-700' : 'text-blue-700'}`}>
                        {regenElapsedMin >= SLOW_AFTER_MIN ? 'Regeneration is taking longer than usual' : 'Regenerating SOW draft...'}
                      </p>
                      <p className={`text-xs mt-0.5 ${regenElapsedMin >= SLOW_AFTER_MIN ? 'text-amber-600' : 'text-blue-500'}`}>
                        {regenElapsedMin >= SLOW_AFTER_MIN
                          ? `Running for ${regenElapsedMin} minutes (normally 5 to 8). Leave it running for now; if it has not finished by 20 minutes, reload the page and regenerate again.`
                          : `AI is re-drafting the scope of work. This normally takes 5 to 8 minutes (${regenElapsedMin >= 1 ? `${regenElapsedMin} min so far` : 'just started'}). You can leave this page open — the new draft is saved when it finishes.`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="max-w-3xl mx-auto px-3 md:px-10 py-4 md:py-8 space-y-6">
                {/* Consultant-only questions — above the document, never inside it */}
                {!editing && (
                  <FollowUpQuestionsPanel
                    questions={followUps}
                    savedAnswers={sow.follow_up_answers}
                    onSave={(answers) => saveSowAnswers(sow.id, answers)}
                    onRegenerate={handleRegenerate}
                    regenerating={regenerating}
                  />
                )}
                {!editing && <AnalysisNotesPanel notes={sow.analysis_notes} />}

                {editing ? (
                  <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-4 md:p-6">
                    <p className="text-[10px] font-semibold text-[#2B8FD8] uppercase tracking-widest mb-3">
                      Editing SOW markdown (v{sow.version})
                    </p>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      data-testid="sow-edit-textarea"
                      className="w-full h-[70vh] font-mono text-[13px] leading-6 text-slate-700 border border-slate-200 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-[#2B8FD8]/30 resize-y"
                      spellCheck={false}
                    />
                  </div>
                ) : (
                  <article className="bg-white rounded-xl border border-slate-200/80 shadow-sm px-4 py-6 md:px-10 md:py-10 lg:px-14 lg:py-12">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {content}
                    </ReactMarkdown>
                  </article>
                )}
              </div>
            </div>

            {/* Right sidebar — Table of Contents */}
            {content && !editing && (
              <div className={`w-[200px] shrink-0 hidden ${isRightPanelOpen ? '2xl:block' : 'xl:block'}`}>
                <div className="sticky top-[100px] py-6 pr-4 max-h-[calc(100vh-120px)] overflow-y-auto">
                  <TableOfContents content={content} activeId={activeTocId} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Chat Panel */}
      <ChatPanel projectId={sow.project_id} selectedDoc={sow} onDocUpdated={handleDocUpdated} isOpen={chatOpen} onClose={() => setChatOpen(false)} />

      {/* Version History Panel */}
      <VersionHistoryPanel
        docId={sow.id}
        docTitle={sow.title}
        isOpen={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        regenerationLineage={sow.regeneration_lineage}
        onNavigateDoc={() => loadSow()}
        onSelectVersion={canEdit ? async (versionContent) => {
          try {
            await axios.patch(`${API_URL_BASE}/documentation/${sow.id}/content`, { content: versionContent });
            toast.success('Version restored!');
            await loadSow();
          } catch { toast.error('Failed to restore version'); }
        } : null}
      />

      {/* Feedback Panel */}
      <FeedbackPanel
        docId={sow.id}
        docContent={content}
        docVersion={sow.version}
        isOpen={feedbackOpen}
        onClose={() => { setFeedbackOpen(false); setFeedbackSection(''); }}
        initialSection={feedbackSection}
        onFeedbackCountChange={handleFeedbackCountChange}
        onApplied={() => loadSow()}
      />

      {/* Floating Chat Toggle */}
      {!chatOpen && !versionHistoryOpen && !feedbackOpen && !editing && (
        <button
          onClick={() => { setChatOpen(true); setVersionHistoryOpen(false); setFeedbackOpen(false); }}
          className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-[#2B8FD8] text-white shadow-lg hover:bg-[#2075b3] transition-all hover:scale-105 flex items-center justify-center group"
          data-testid="sow-chat-toggle-btn"
        >
          <Sparkles className="h-5 w-5 group-hover:rotate-12 transition-transform" />
        </button>
      )}
    </Layout>
  );
};

export default SowDraftPage;
